import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormSection,
  Input,
  Switch,
} from '@/components/ui';
import { useTranslation } from '@/lib/i18n';
import { RangeRepeatFields } from '@/components/shipping/RangeRepeatFields';
import { validateRangeRepeat, type RangeRepeat } from '@/lib/domain/rangeRepeat';
import { REGION_AREA_NAME_MAX, validateRegionSurcharges } from '@/lib/domain/regionSurcharge';
import { RadioRow } from './RadioRow';
import { ShippingFeeTierEditor } from './ShippingFeeTierEditor';
import { RegionSurchargeEditor } from './RegionSurchargeEditor';
import {
  isTieredFeeType,
  PAYMENT_METHODS,
  SHIPPING_FEE_TYPES,
  SHIPPING_METHODS,
  SUPPLIER_SCOPES,
  UNLIMITED_MAX_BOUND,
  type PaymentMethod,
  type ShippingFeePolicyDetail,
  type ShippingFeePolicyPayload,
  type ShippingFeeRegionSurcharge,
  type ShippingFeeTier,
  type ShippingFeeType,
  type ShippingMethod,
  type SupplierScope,
} from '../types/shippingFee';

interface ShippingFeePolicyEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 편집 대상(상세). null 이면 신규. */
  target: ShippingFeePolicyDetail | null;
  saving: boolean;
  onSubmit: (payload: ShippingFeePolicyPayload) => void;
}

interface EditorDraft {
  name: string;
  supplierScope: SupplierScope;
  supplierKey: string;
  shippingMethod: ShippingMethod;
  feeType: ShippingFeeType;
  fixedFee: string;
  paymentMethod: PaymentMethod;
  regionSurchargeEnabled: boolean;
  isDefault: boolean;
  tiers: ShippingFeeTier[];
  regionSurcharges: ShippingFeeRegionSurcharge[];
  rangeRepeat: RangeRepeat | null;
}

/**
 * 범위반복의 기준 구간 — 가장 낮은 구간(minBound 가 제일 작은 행). BE 도 "첫 구간" 을 이렇게 본다.
 * 상한이 무제한 sentinel 이면 켤 때의 시작값 기본으로 쓰지 않는다.
 */
export const firstTierBasis = (tiers: ReadonlyArray<ShippingFeeTier>) => {
  if (tiers.length === 0) return { baseFee: null, upperBound: null };
  const first = tiers.reduce((a, b) => (b.minBound < a.minBound ? b : a));
  return {
    baseFee: first.fee,
    upperBound: first.maxBound >= UNLIMITED_MAX_BOUND ? null : first.maxBound,
  };
};

const emptyDraft = (): EditorDraft => ({
  name: '',
  supplierScope: 'HQ',
  supplierKey: '',
  shippingMethod: 'COURIER',
  feeType: 'FIXED',
  fixedFee: '0',
  paymentMethod: 'PREPAID',
  regionSurchargeEnabled: false,
  isDefault: false,
  tiers: [],
  regionSurcharges: [],
  rangeRepeat: null,
});

const toDraft = (target: ShippingFeePolicyDetail): EditorDraft => ({
  name: target.name,
  supplierScope: target.supplierScope,
  supplierKey: target.supplierKey ?? '',
  shippingMethod: target.shippingMethod,
  feeType: target.feeType,
  fixedFee: String(target.fixedFee),
  paymentMethod: target.paymentMethod,
  regionSurchargeEnabled: target.regionSurchargeEnabled,
  isDefault: target.isDefault,
  tiers: target.tiers.map((t) => ({ ...t })),
  regionSurcharges: target.regionSurcharges.map((r) => ({ ...r })),
  // 반복이 없으면 응답에서 필드가 빠진다.
  rangeRepeat: target.rangeRepeat ? { ...target.rangeRepeat } : null,
});

const num = (v: string): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * ID50 배송비조건 생성/편집 다이얼로그. (고도몰 ID50_2 수정폼.)
 * 배송비유형(라디오) → 유형별 동적 입력(고정액 / BY_* 구간 테이블 / 무료 없음),
 * 결제방법 라디오, 지역별 추가배송비 토글. 검증은 be-shipping 계약 미러링.
 */
export const ShippingFeePolicyEditor = ({
  open,
  onOpenChange,
  target,
  saving,
  onSubmit,
}: ShippingFeePolicyEditorProps) => {
  const { t } = useTranslation('shippingFee');
  const [draft, setDraft] = useState<EditorDraft>(() =>
    target ? toDraft(target) : emptyDraft(),
  );
  const [error, setError] = useState<string | null>(null);

  const supplierScopeLabels: Record<SupplierScope, string> = {
    ALL: t('shippingFee.supplierScope.ALL'),
    HQ: t('shippingFee.supplierScope.HQ'),
    SUPPLIER: t('shippingFee.supplierScope.SUPPLIER'),
  };
  const shippingMethodLabels: Record<ShippingMethod, string> = {
    COURIER: t('shippingFee.shippingMethod.COURIER'),
    VISIT: t('shippingFee.shippingMethod.VISIT'),
    QUICK: t('shippingFee.shippingMethod.QUICK', '퀵 배송'),
  };
  const feeTypeLabels: Record<ShippingFeeType, string> = {
    FIXED: t('shippingFee.feeType.FIXED'),
    BY_AMOUNT: t('shippingFee.feeType.BY_AMOUNT'),
    BY_QTY: t('shippingFee.feeType.BY_QTY'),
    BY_WEIGHT: t('shippingFee.feeType.BY_WEIGHT'),
    FREE: t('shippingFee.feeType.FREE'),
  };
  const paymentMethodLabels: Record<PaymentMethod, string> = {
    PREPAID: t('shippingFee.paymentMethod.PREPAID'),
    COD: t('shippingFee.paymentMethod.COD'),
    CHOICE: t('shippingFee.paymentMethod.CHOICE'),
  };

  useEffect(() => {
    if (open) {
      setDraft(target ? toDraft(target) : emptyDraft());
      setError(null);
    }
  }, [open, target]);

  const set = <K extends keyof EditorDraft>(key: K, val: EditorDraft[K]) =>
    setDraft((prev) => ({ ...prev, [key]: val }));

  // BE 는 regionSurchargeEnabled=false 면 지역 행을 저장하지 않는다(구간·지역을 지우고 요청대로 다시 만든다).
  // 끄고 저장하면 목록이 사라지고 다시 켜도 비어 있다 — 기본 템플릿이면 전 상품의 제주 추가배송비가 없어진다.
  // 그래서 목록이 있을 때 끄려면 한 번 확인한다. 저장 전에 다시 켜면 목록은 그대로 돌아온다.
  const handleRegionToggle = (next: boolean) => {
    if (!next && draft.regionSurcharges.length > 0) {
      const ok = window.confirm(
        t('shippingFee.editor.regionSurchargeOffConfirm', {
          count: draft.regionSurcharges.length,
          defaultValue:
            '지역별 추가배송비를 끄고 저장하면 지역 {{count}}곳이 삭제됩니다. 다시 켜도 목록은 비어 있습니다. 끄시겠습니까?',
        }),
      );
      if (!ok) return;
    }
    set('regionSurchargeEnabled', next);
  };

  const tiered = isTieredFeeType(draft.feeType);
  const isVisit = draft.shippingMethod === 'VISIT';

  const validate = (): string | null => {
    if (draft.name.trim() === '') return t('shippingFee.validation.nameRequired');
    if (draft.supplierScope === 'SUPPLIER' && draft.supplierKey.trim() === '') {
      return t('shippingFee.validation.supplierKeyRequired');
    }
    // 구간형(BY_*)은 tiers 1개 이상 + maxBound > minBound (be-shipping 400 미러링).
    if (tiered) {
      if (draft.tiers.length === 0) return t('shippingFee.validation.tierRequired');
      for (const tier of draft.tiers) {
        if (tier.maxBound <= tier.minBound) {
          return t('shippingFee.validation.tierBoundInvalid');
        }
      }
      // 범위반복 — be-order RangeRepeatPayload 와 같은 기준. 구간 1개 이상은 위에서 이미 보장된다.
      if (draft.rangeRepeat) {
        const problem = validateRangeRepeat(draft.rangeRepeat);
        if (problem) {
          const fallback = {
            start: '범위반복 시작값은 0 이상이어야 합니다.',
            unit: '범위반복 단위는 0 보다 커야 합니다.',
            fee: '범위반복 추가요금은 0 이상의 정수(원)여야 합니다.',
          }[problem];
          return t(`shippingFee.validation.rangeRepeat_${problem}`, { defaultValue: fallback });
        }
      }
    }
    // 지역 추가배송비 — 지역명이 매칭 기준이라 필수(be-order RegionSurchargeUpsert, order V62).
    if (draft.regionSurchargeEnabled) {
      const problem = validateRegionSurcharges(
        draft.regionSurcharges.map((r) => ({ areaName: r.label, fee: r.surchargeFee })),
      );
      if (problem) {
        const fallback = {
          nameRequired: '지역 추가배송비 {{row}}번째 행의 지역명을 입력하세요.',
          nameTooLong: '지역 추가배송비 {{row}}번째 행의 지역명은 {{max}}자 이하로 입력하세요.',
          fee: '지역 추가배송비 {{row}}번째 행의 추가 배송비는 0 이상의 정수(원)여야 합니다.',
        }[problem.kind];
        return t(`shippingFee.validation.region_${problem.kind}`, {
          row: problem.index + 1,
          max: REGION_AREA_NAME_MAX,
          defaultValue: fallback,
        });
      }
    }
    return null;
  };

  const handleSubmit = () => {
    const message = validate();
    if (message) {
      setError(message);
      return;
    }
    setError(null);
    onSubmit({
      name: draft.name.trim(),
      supplierScope: draft.supplierScope,
      // SUPPLIER 가 아니면 supplierKey 는 BE 가 null 정규화하지만 FE 도 선제 정규화.
      supplierKey:
        draft.supplierScope === 'SUPPLIER' && draft.supplierKey.trim() !== ''
          ? draft.supplierKey.trim()
          : null,
      shippingMethod: draft.shippingMethod,
      feeType: draft.feeType,
      // FIXED 일 때만 의미. 그 외 0.
      fixedFee: draft.feeType === 'FIXED' ? num(draft.fixedFee) : 0,
      paymentMethod: draft.paymentMethod,
      regionSurchargeEnabled: draft.regionSurchargeEnabled,
      // 기본 템플릿 토글. single-default 해제는 BE 트랜잭션이 처리(BO 는 값만 전송).
      isDefault: draft.isDefault,
      // 구간형이 아니면 tiers 비우기(BE 무시하지만 명확히).
      tiers: tiered ? draft.tiers.map((t) => ({ ...t })) : [],
      // 토글 OFF 면 지역가산 비우기(BE 도 저장하지 않는다 — 끌 때 확인을 받는다). 켜져 있으면 검색·페이지와
      // 무관하게 전체 목록을 보낸다: BE 가 지역을 전부 지우고 요청대로 다시 만들어, 빠진 행은 사라진다.
      // 지역명은 매칭 기준이라 앞뒤 공백을 떼어 보낸다.
      regionSurcharges: draft.regionSurchargeEnabled
        ? draft.regionSurcharges.map((r) => ({ ...r, label: r.label.trim() }))
        : [],
      // 유형과 무관하게 항상 보낸다 — PUT 전체 교체라 빼면 반복이 해제된다. FIXED/FREE 로 저장하면 BE 가
      // 반복을 지운다(applyRangeRepeat). 불러온 값이 남는 건 저장 전 편집 중에 유형을 되돌릴 때뿐이다.
      rangeRepeat: draft.rangeRepeat ? { ...draft.rangeRepeat } : null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[760px]">
        <DialogHeader>
          <DialogTitle>
            {target ? t('shippingFee.editor.titleEdit') : t('shippingFee.editor.titleCreate')}
          </DialogTitle>
          <DialogDescription>
            {t('shippingFee.editor.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex max-h-[66vh] flex-col gap-1 overflow-auto py-1">
          {/* 기본 템플릿은 상품이 따로 고르지 않으면 쓰이는 조건이라, 고치면 판매중 상품 전체의 배송비가 바뀐다. */}
          {target?.isDefault ? (
            <div
              className="mb-2 rounded-md px-3 py-2 text-[13px]"
              style={{ background: 'var(--status-warn-bg)', color: 'var(--status-warn-fg)' }}
              data-testid="shipping-fee-default-notice"
            >
              {t('shippingFee.editor.defaultTemplateNotice', {
                defaultValue: '판매중 상품 전체에 적용되는 기본 템플릿입니다. 저장하면 모든 상품의 배송비가 바뀝니다.',
              })}
            </div>
          ) : null}

          {error ? (
            <div
              className="mb-2 rounded-md px-3 py-2 text-[13px]"
              style={{
                background: 'var(--status-danger-bg)',
                color: 'var(--status-danger-fg)',
              }}
              role="alert"
            >
              {error}
            </div>
          ) : null}

          <FormSection label={t('shippingFee.editor.name')} density="compact" required>
            <Input
              value={draft.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder={t('shippingFee.editor.namePlaceholder')}
              maxLength={100}
              disabled={saving}
              data-testid="shipping-fee-name"
            />
          </FormSection>

          <RadioRow
            name="sfp-supplier-scope"
            label={t('shippingFee.editor.scope')}
            options={SUPPLIER_SCOPES}
            labels={supplierScopeLabels}
            value={draft.supplierScope}
            onChange={(v) => set('supplierScope', v)}
            disabled={saving}
          />

          {draft.supplierScope === 'SUPPLIER' ? (
            <FormSection label={t('shippingFee.editor.supplierKey')} density="compact" required>
              <Input
                value={draft.supplierKey}
                onChange={(e) => set('supplierKey', e.target.value)}
                placeholder={t('shippingFee.editor.supplierKeyPlaceholder')}
                maxLength={64}
                disabled={saving}
                className="max-w-[280px]"
              />
            </FormSection>
          ) : null}

          <RadioRow
            name="sfp-shipping-method"
            label={t('shippingFee.editor.method')}
            options={SHIPPING_METHODS}
            labels={shippingMethodLabels}
            value={draft.shippingMethod}
            onChange={(v) => set('shippingMethod', v)}
            disabled={saving}
          />

          {isVisit ? (
            <p
              className="px-1 pb-2 text-[12px]"
              style={{ color: 'var(--content-muted)' }}
            >
              {t('shippingFee.editor.visitNotice')}
            </p>
          ) : null}

          <RadioRow
            name="sfp-fee-type"
            label={t('shippingFee.editor.feeType')}
            options={SHIPPING_FEE_TYPES}
            labels={feeTypeLabels}
            value={draft.feeType}
            onChange={(v) => set('feeType', v)}
            disabled={saving}
          />

          {/* 배송비유형별 동적 입력 */}
          {draft.feeType === 'FIXED' ? (
            <FormSection label={t('shippingFee.editor.fixedFee')} density="compact">
              <Input
                type="number"
                step="1"
                min={0}
                value={draft.fixedFee}
                onChange={(e) => set('fixedFee', e.target.value)}
                disabled={saving}
                className="max-w-[200px] tabular-nums"
                data-testid="shipping-fee-fixed"
              />
            </FormSection>
          ) : null}

          {tiered ? (
            <div className="py-2">
              <p
                className="mb-2 text-[13px] font-semibold"
                style={{ color: 'var(--content-strong)' }}
              >
                {t('shippingFee.editor.tierTitle')}
              </p>
              <ShippingFeeTierEditor
                tiers={draft.tiers}
                feeType={draft.feeType as 'BY_AMOUNT' | 'BY_QTY' | 'BY_WEIGHT'}
                disabled={saving}
                onChange={(tiers) => set('tiers', tiers)}
              />
              {/* 고도몰 배송조건 "범위반복"(order V61). 구간형 전용 — FIXED/FREE 면 이 블록째 숨는다. */}
              <div className="mt-2">
                <RangeRepeatFields
                  value={draft.rangeRepeat}
                  onChange={(rangeRepeat) => set('rangeRepeat', rangeRepeat)}
                  unitLabel={t(`shippingFee.tierUnit.${draft.feeType}`)}
                  baseFee={firstTierBasis(draft.tiers).baseFee}
                  firstUpperBound={firstTierBasis(draft.tiers).upperBound}
                  hasTiers={draft.tiers.length > 0}
                  disabled={saving}
                />
              </div>
            </div>
          ) : null}

          {draft.feeType === 'FREE' ? (
            <p className="px-1 py-2 text-[12px]" style={{ color: 'var(--content-muted)' }}>
              {t('shippingFee.editor.freeNotice')}
            </p>
          ) : null}

          <RadioRow
            name="sfp-payment-method"
            label={t('shippingFee.editor.paymentMethod')}
            options={PAYMENT_METHODS}
            labels={paymentMethodLabels}
            value={draft.paymentMethod}
            onChange={(v) => set('paymentMethod', v)}
            disabled={saving}
          />

          <FormSection label={t('shippingFee.editor.regionSurcharge')} density="compact">
            <Switch
              checked={draft.regionSurchargeEnabled}
              onCheckedChange={handleRegionToggle}
              disabled={saving}
              aria-label={t('shippingFee.editor.regionSurchargeAria')}
            />
          </FormSection>

          {/* 확인 후 끈 상태 — 저장 전이라 목록은 아직 남아 있다. 저장하면 지워진다는 사실을 계속 보여 준다. */}
          {!draft.regionSurchargeEnabled && draft.regionSurcharges.length > 0 ? (
            <p className="py-1 text-[12px]" style={{ color: 'var(--status-danger-fg)' }} data-testid="region-surcharge-off-warning">
              {t('shippingFee.editor.regionSurchargeOffWarning', {
                count: draft.regionSurcharges.length,
                defaultValue: '저장하면 지역 {{count}}곳이 삭제됩니다. 유지하려면 다시 켜세요.',
              })}
            </p>
          ) : null}

          {draft.regionSurchargeEnabled ? (
            <div className="py-2">
              <RegionSurchargeEditor
                surcharges={draft.regionSurcharges}
                disabled={saving}
                onChange={(rows) => set('regionSurcharges', rows)}
              />
            </div>
          ) : null}

          {/* M-8 ID81 기본 템플릿 — 1개만 지정(BE single-default enforce), 신규 상품 자동선택. */}
          <FormSection label={t('shippingFee.editor.isDefault')} density="compact">
            <Switch
              checked={draft.isDefault}
              onCheckedChange={(v) => set('isDefault', v)}
              disabled={saving}
              aria-label={t('shippingFee.editor.isDefaultAria')}
              data-testid="shipping-fee-default"
            />
          </FormSection>
          {draft.isDefault ? (
            <p className="px-1 pb-2 text-[12px]" style={{ color: 'var(--content-muted)' }}>
              {t('shippingFee.editor.isDefaultNotice')}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            {t('shippingFee.editor.cancel')}
          </Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={saving}
            data-testid="shipping-fee-save"
          >
            {saving ? t('shippingFee.editor.saving') : t('shippingFee.editor.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** 무제한 상한 표기 헬퍼 export (목록 상세보기 등에서 재사용). */
export const formatBound = (v: number): string =>
  v >= UNLIMITED_MAX_BOUND ? '무제한' : v.toLocaleString('ko-KR');
